import React, { ComponentType, ReactNode } from 'react';

const INVALID_VALUES = /\b(?:undefined|null|NaN|Invalid Date)\b/g;
const HAS_INVALID_VALUE = /\b(?:undefined|null|NaN|Invalid Date)\b/;
const DANGLING_SEPARATORS = /(^|\s)[-–—|/•·.:;]+(?=\s|$)/g;
const DANGLING_COMMAS = /\s*,\s*(?=,|$)/g;
const SEPARATORS_ONLY = /^[\s,;:.|/•·\-–—]*$/;

export const stripInvalidValues = (text: string): string => {
    if (!HAS_INVALID_VALUE.test(text)) {
        return text;
    }

    return text
        .replace(INVALID_VALUES, ' ')
        .replace(DANGLING_SEPARATORS, ' ')
        .replace(DANGLING_COMMAS, '')
        .replace(/^\s*,\s*/, '')
        .replace(/\s+/g, ' ')
        .trim();
};

export const joinMetaDataValues = (...values: unknown[]): string =>
    values
        .map((value) => (typeof value === 'string' || typeof value === 'number' ? stripInvalidValues(`${value}`) : ''))
        .filter((value) => !SEPARATORS_ONLY.test(value))
        .join(' ');

const stripInvalidNodeValue = (node: ReactNode): ReactNode => {
    if (typeof node === 'string') {
        return stripInvalidValues(node);
    }

    if (typeof node === 'number') {
        return Number.isFinite(node) ? node : '';
    }

    return node;
};

const isRenderable = (node: ReactNode): boolean => {
    if (typeof node === 'string') {
        return !SEPARATORS_ONLY.test(node);
    }

    return node !== null && node !== undefined && typeof node !== 'boolean';
};

export const withMetaDataGuard = <T extends ComponentType<any>>(WrappedComponent: T): T => {
    const GuardedMetaData = ({ children, ...props }: { children?: ReactNode }) => {
        const strippedChildren = React.Children.toArray(children).map(stripInvalidNodeValue);

        if (!strippedChildren.some(isRenderable)) {
            return null;
        }

        const Component = WrappedComponent as ComponentType<any>;

        return <Component {...props}>{strippedChildren}</Component>;
    };

    GuardedMetaData.displayName = `withMetaDataGuard(${(WrappedComponent as any).displayName || 'Component'})`;

    return GuardedMetaData as unknown as T;
};
